/**
 * "Importar desde un enlace" — the Upload page's link import (back end: convex/intake.ts).
 *
 * The media intake service fetches the address; Circa copies the file into the library and then
 * processes it like any upload. Availability comes from the back end (the switch and its
 * settings), so the panel never offers an action the back end would refuse. The role check is the
 * back end's (`requirePermission(ctx, "asset_create")` in `startLinkImport`); the page wraps this
 * panel in `AccessGate perm="asset_create"`.
 */
import { useEffect, useRef, useState } from 'react';
import { useAction, useQuery } from 'convex/react';
import { ConvexError } from 'convex/values';
import { api } from '@convex/api';
import { Btn, Field, Input, Panel, Select } from '../../components/primitives';
import { navigate } from '../../lib/nav';
import { linkImportErrorMessage } from '../../lib/intake/intakePhase';
import type { AssetType } from '../../lib/adapters';

const NOTE_STYLE = { fontSize: 13, color: 'var(--muted-slate)', lineHeight: 1.5, marginTop: 12 };

export function LinkImportPanel({ assetTypeOptions }: { assetTypeOptions: ReadonlyArray<readonly [AssetType, string]> }) {
  const availability = useQuery(api.intake.linkImportAvailable, {});
  const startLinkImport = useAction(api.intake.startLinkImport);
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [assetType, setAssetType] = useState<AssetType>('vod');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const anchor = useRef<HTMLDivElement | null>(null);

  // The toolbar item "Desde URL" opens /library/upload#enlace.
  useEffect(() => {
    if (window.location.hash === '#enlace') anchor.current?.scrollIntoView?.({ block: 'start' });
  }, []);

  const unavailableText =
    availability !== undefined && !availability.available
      ? linkImportErrorMessage(availability.reason === 'disabled' ? 'intake_disabled' : 'intake_misconfigured')
      : undefined;
  const locked = busy || availability === undefined || unavailableText !== undefined;

  async function onImport() {
    setBusy(true);
    setError(undefined);
    try {
      const { assetId } = await startLinkImport({ url, title: title.trim() === '' ? undefined : title, assetType });
      navigate(`/library/${assetId}`);
    } catch (err) {
      const data = err instanceof ConvexError ? (err.data as { code?: string; retryAfterSeconds?: number }) : undefined;
      setError(linkImportErrorMessage(data?.code, data?.retryAfterSeconds));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div ref={anchor} id="enlace">
      <Panel title="Importar desde un enlace">
        <Field
          label="Enlace del video"
          hint="MP4, MOV, MKV o WebM, hasta 10 gigabytes. El enlace debe ser público y comenzar con https://."
          error={error}
        >
          <Input value={url} onChange={setUrl} placeholder="https://…" icon="linkIcon" disabled={locked} />
        </Field>
        <div style={{ height: 12 }} />
        <Field label="Título (opcional)">
          <Input value={title} onChange={setTitle} placeholder="Si lo dejas vacío se usa el nombre del archivo" disabled={locked} />
        </Field>
        <div style={{ height: 12 }} />
        <Field label="Tipo de contenido">
          <Select
            value={assetType}
            onChange={(value: string) => setAssetType(value as AssetType)}
            full
            options={assetTypeOptions.map(([value, label]) => ({ value, label }))}
          />
        </Field>
        <Btn variant="solid" full loading={busy} disabled={locked || url.trim() === ''} style={{ marginTop: 12 }} onClick={onImport}>
          Importar
        </Btn>
        {unavailableText !== undefined ? (
          <p style={NOTE_STYLE}>{unavailableText}</p>
        ) : (
          <p style={NOTE_STYLE}>
            El archivo se copia a la biblioteca y luego se procesa como cualquier subida. Puedes seguir su avance en la ficha del video.
          </p>
        )}
      </Panel>
    </div>
  );
}
